<template>
  <div class="completed-hikes-app">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="display-4 m-0"><strong>Completed Hikes</strong></h1>
    </div>

    <hr class="my-3">

    <!-- Filters (same set as the Hikes list, minus "hide completed") -->
    <HikeFilterBar
      ref="hikeFilterBar"
      :feature-tag-options="featureTagOptions"
      :activity-tag-options="activityTagOptions"
      @change="onFiltersChange"
    />

    <!-- Loading State -->
    <div v-if="loading && completedHikes.length === 0" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && completedHikes.length === 0" class="no-completed-hikes">
      <span class="material-icons">check_circle</span>
      <p class="text-muted mb-2">{{ emptyStateMessage }}</p>
      <p v-if="!hasActiveFilters" class="text-muted"><small>Mark hikes as completed from the hike details page.</small></p>
    </div>

    <!-- Completed Hikes List -->
    <div v-else class="completed-hikes-list">
      <CompletedHikeItem
        v-for="completedHike in completedHikes"
        :key="completedHike.id"
        :completed-hike="completedHike"
        @show-flyover="showFlyover"
        @export-gpx="exportGpx"
        @copy-share="triggerCopyModel"
        @add-to-list="launchAddToList"
        @request-download="handleDownloadRequest"
      />

      <!-- Load More -->
      <div v-if="hasNextPage" class="see-more d-flex justify-content-center m-0">
        <button class="btn btn-link" @click="loadMore" :disabled="loading">
          {{ loading ? 'Loading...' : 'See More' }}
        </button>
      </div>

      <!-- Total Count -->
      <div v-if="totalCount > 0" class="mt-4 text-center">
        <p class="text-muted">
          <small>{{ totalCountMessage }}</small>
        </p>
      </div>
    </div>

    <CopyLinkModal ref="shareModal" helper-text="Copy the link to share this hike" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import CompletedHikeItem from './components/CompletedHikeItem.vue'
import HikeFilterBar from '../components/HikeFilterBar.vue'
import CopyLinkModal from '../../utils/shared_components/CopyLinkModal.vue'
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal'

const props = defineProps({
  // 'current' (logged-in user's own list) or 'public' (another user's profile)
  context: { type: String, default: 'current' },
  firebaseUserId: { type: String, default: null }
})

const isPublic = computed(() => props.context === 'public')

const completedHikes = ref([])
const loading = ref(false)
const currentPage = ref(1)
const totalCount = ref(0)
const hasNextPage = ref(false)
const shareModal = ref(null)
const hikeFilterBar = ref(null)
const featureTagOptions = ref([])
const activityTagOptions = ref([])
// The applied filter variables, kept in sync via HikeFilterBar's `change` event.
const appliedVariables = ref({})

const hasActiveFilters = computed(() => Object.values(appliedVariables.value).some(activeValue))

const emptyStateMessage = computed(() => {
  if (hasActiveFilters.value) return 'No completed hikes match these filters.'
  return isPublic.value ? "This user hasn't completed any public hikes yet." : "You haven't completed any hikes yet."
})

const totalCountMessage = computed(() => {
  const noun = totalCount.value === 1 ? 'hike' : 'hikes'
  return isPublic.value
    ? `${totalCount.value} completed ${noun}`
    : `You've completed ${totalCount.value} ${noun}! 🎉`
})

const COMPLETED_HIKE_FIELDS = `
  completedHikes {
    id
    completedAt
    notes
    hike {
      id
      name
      slug
      obfuscatedSlug
      difficulty
      distanceMeters
      elevationGainMeters
      publiclyVisible
      shareableUrl
      processingStatus
      mainCountry { slug name }
      mainRegion { slug name }
      staticMapImageUrl
      squareStaticMapImageUrl
    }
  }
  totalCount
  currentPage
  hasNextPage
`

const FILTER_VAR_DECLS = `$query: String, $distanceMetersMin: Int, $distanceMetersMax: Int,
  $elevationGainMetersMin: Int, $elevationGainMetersMax: Int, $difficulties: [String!],
  $featureTags: [String!], $activityTags: [String!], $startDate: ISO8601DateTime,
  $endDate: ISO8601DateTime, $sort: String`

const FILTER_ARGS = `query: $query, distanceMetersMin: $distanceMetersMin, distanceMetersMax: $distanceMetersMax,
  elevationGainMetersMin: $elevationGainMetersMin, elevationGainMetersMax: $elevationGainMetersMax,
  difficulties: $difficulties, featureTags: $featureTags, activityTags: $activityTags,
  startDate: $startDate, endDate: $endDate, sort: $sort`

function completedHikesQuery () {
  if (isPublic.value) {
    return `query PublicCompletedHikes($page: Int!, $perPage: Int!, $firebaseUserId: String!, ${FILTER_VAR_DECLS}) {
      publicUserCompletedHikes(firebaseUserId: $firebaseUserId, page: $page, perPage: $perPage, ${FILTER_ARGS}) { ${COMPLETED_HIKE_FIELDS} }
    }`
  }
  return `query UserCompletedHikes($page: Int!, $perPage: Int!, ${FILTER_VAR_DECLS}) {
    userCompletedHikes(page: $page, perPage: $perPage, ${FILTER_ARGS}) { ${COMPLETED_HIKE_FIELDS} }
  }`
}

function activeValue (value) {
  if (value === null || value === undefined) return false
  if (Array.isArray(value)) return value.length > 0
  if (value === 'created_desc') return false // default sort is not an "active" filter
  return true
}

async function postGraphql (query, variables) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  const response = await fetch('/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
    body: JSON.stringify({ query, variables })
  })
  return response.json()
}

async function fetchCompletedHikes (page = 1) {
  loading.value = true
  try {
    const variables = { page, perPage: 20, ...appliedVariables.value }
    if (isPublic.value) variables.firebaseUserId = props.firebaseUserId

    const result = await postGraphql(completedHikesQuery(), variables)
    const root = isPublic.value ? 'publicUserCompletedHikes' : 'userCompletedHikes'
    const data = result.data?.[root]
    if (data) {
      completedHikes.value = page === 1 ? (data.completedHikes || []) : completedHikes.value.concat(data.completedHikes || [])
      totalCount.value = data.totalCount || 0
      currentPage.value = data.currentPage || 1
      hasNextPage.value = data.hasNextPage || false
    }
  } catch (error) {
    console.error('Error fetching completed hikes:', error)
  } finally {
    loading.value = false
  }
}

async function loadHikeTags () {
  try {
    const result = await postGraphql('query { allHikeTags { featureTags activityTags } }', {})
    const tags = result.data?.allHikeTags
    featureTagOptions.value = Array.isArray(tags?.featureTags) ? tags.featureTags : []
    activityTagOptions.value = Array.isArray(tags?.activityTags) ? tags.activityTags : []
  } catch (error) {
    console.error('Error loading hike tags:', error)
  }
}

function onFiltersChange (variables) {
  appliedVariables.value = variables || {}
  fetchCompletedHikes(1)
}

function loadMore () {
  if (!loading.value && hasNextPage.value) {
    fetchCompletedHikes(currentPage.value + 1)
  }
}

function showFlyover (event, completedHike) {
  if (event && event.preventDefault) event.preventDefault()
  if (window.showMapModalForTrail && completedHike.hike?.id) {
    window.showMapModalForTrail(completedHike.hike.id)
  } else {
    console.error('Flyover modal launcher not found (showMapModalForTrail)')
  }
}

function exportGpx (completedHike) {
  console.log('Export GPX for:', completedHike)
}

function triggerCopyModel (event, completedHike) {
  if (shareModal.value && completedHike.hike?.shareableUrl) {
    shareModal.value.show(`${completedHike.hike.shareableUrl}`, completedHike.hike, 'hike')
  }
}

function launchAddToList (completedHike) {
  const hike = completedHike.hike
  if (window.onBoardingVue) {
    if (typeof window.onBoardingVue.setAddToListContext === 'function' && hike?.id) {
      window.onBoardingVue.setAddToListContext({ hikeId: hike.id })
    }
    if (typeof window.onBoardingVue.launchFor === 'function') {
      window.onBoardingVue.launchFor(OnBoardingGoal.AddTrailToList)
    }
  }
}

function handleDownloadRequest (completedHike) {
  try {
    const hike = completedHike.hike
    if (!hike) return
    if (hike.mainCountry?.slug && hike.mainRegion?.slug && hike.slug) {
      window.country_slug = hike.mainCountry.slug
      window.region_slug = hike.mainRegion.slug
      window.slug = hike.slug
    }
    if (hike.id) {
      window.trail_id = hike.id
      window.trailId = hike.id
      window.hike_id = hike.id
      window.id = hike.id
    }
    if (hike.name) {
      window.trail_name = hike.name
      window.trailName = hike.name
    }
    if (hike.shareableUrl) {
      window.trail_share_url = hike.shareableUrl
      try {
        window.trail_download_path = new URL(hike.shareableUrl, window.location.origin).pathname
      } catch (_) {
        window.trail_download_path = hike.shareableUrl
      }
    }
    if (window.openDownloadModal) window.openDownloadModal()
  } catch (error) {
    console.error('Error opening download modal:', error)
  }
}

onMounted(() => {
  loadHikeTags()
  fetchCompletedHikes(1)
})
</script>

<style scoped>
.completed-hikes-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.no-completed-hikes {
  text-align: center;
  padding: 4rem 2rem;
}

.no-completed-hikes .material-icons {
  font-size: 64px;
  color: rgba(255, 255, 255, 0.2);
  margin-bottom: 1rem;
}

.see-more {
  margin-top: 1rem;
}

.see-more .btn-link {
  color: #FBCD01;
  text-decoration: none;
  font-weight: 600;
}

.see-more .btn-link:hover {
  text-decoration: underline;
}
</style>
